import { exportToCanvas } from '@excalidraw/excalidraw';
import { ExcalidrawElement } from '@excalidraw/excalidraw/types/element/types';
import { BinaryFiles, ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types/types';
import { useFlag } from '@unleash/proxy-client-react';
import { Collapse, Divider, Modal, Slider, Tooltip } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import { DeleteSvg, QuestionMarkSvg } from '../../../../assets/icons';
import EnterprisePlanIcon from '../../../../assets/icons/EnterprisePlanIcon';
import { MagicWandSvg } from '../../../../assets/icons/MagicWandSvg';
import theme from '../../../../assets/theme';
import useAccessBadge, { LicenseCoarseLevels } from '../../../../hooks/useAccessBadge';
import {
  allPromptOutputsSelector,
  attachmentsSelector,
  useDesignerDataContext,
} from '../../../../hooks/useDesignerLogic';
import {
  DesignerUIStateActions,
  useDesignerUIContext,
} from '../../../../hooks/useDesignerUIState';
import { useGameMediaFileUploader } from '../../../../hooks/useGameMediaFileUploader';
import { useMetrics } from '../../../../hooks/useMetrics';
import usePaywallPrompt from '../../../../hooks/usePaywallPrompt';
import useRouterMatch from '../../../../hooks/useRouterMatch';
import useSingleSlidePromptTestCase from '../../../../hooks/useSingleSlidePromptTestCase';
import {
  getAvailablePromptDependencies,
  useVisiblePromptDependentInputValues,
  useVisiblePromptTestCaseInputs,
} from '../../../../hooks/useVisiblePromptTestCaseInputs';
import {
  ById,
  LocalPromptDependency,
  Prompt,
  PromptInput,
  PromptModels,
  promptModels,
  PromptSingleTestCaseResultType,
} from '../../../../services/backendService/types';
import { generateUniqueId } from '../../../../utils/general';
import { Input } from '../../../_atoms/_Input';
import Select from '../../../_atoms/_Select/Select';
import { TextArea } from '../../../_atoms/_TextArea';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import { DrawingBuilder } from '../../../_molecules/GameDrawingBackgroundSelector/GameDrawingBackgroundSelector';
import SuspensePostHydration from '../../../_molecules/SuspensePostHydration';
import { parseSlateStringToText } from '../../../SlideComponents/slateUtil';
import {
  ExistingDrawing,
  Slide,
  SlideElementImageType,
} from '../../../SlideComponents/slide.types';
import UploadFilePicker from '../../UploadFilePicker';
import { UploadFilePickerFile } from '../../UploadFilePicker/UploadFilePicker';
import {
  AiDrawingFeedbackType,
  createAiFeedbackPrompt,
  createAiMultiModalFeedbackPrompt,
  getDrawingPromptTemplate,
} from '../PromptsTab/newPromptTemplates';
import { findDependencies } from '../PromptsTab/PromptBuilder';
import { PromptTextEditor } from '../PromptsTab/PromptTextEditor';

const AiFeedbackImagePromptBuilder = ({
  onRoundChange,
  slide,
  promptInputs,
  presentationId,
  isGenerator,
}: {
  onRoundChange: (slide: Slide) => void;
  slide: Slide;
  promptInputs?: PromptInput[];
  presentationId: string;
  isGenerator?: boolean;
}) => {
  const promptDependentInputs = useDesignerUIContext(
    context => context.state.promptDependentInputs,
  );
  const dispatch = useDesignerUIContext(context => context.dispatch);

  const isPassagesEnabled = useFlag('nativepassages');

  const lessonAttachments = useDesignerDataContext(attachmentsSelector);

  const [isEditingPrompt, setIsEditingPrompt] = useState<boolean>(false);
  const { tenantId } = useRouterMatch();
  const { upload, isSuccess, isUploading } = useGameMediaFileUploader(tenantId);

  const [showDrawingMediaModal, setShowDrawingMediaModal] = useState<boolean>(false);
  const [imageToUpload, setImageToUpload] = useState<UploadFilePickerFile | undefined>();
  const metrics = useMetrics();
  const { generate, isLoading } = useSingleSlidePromptTestCase(presentationId, slide.id);

  const prompt = useMemo(() => {
    if (slide.prompts?.[0]) return slide.prompts?.[0];
    return createAiFeedbackPrompt();
  }, [slide]);

  const testCaseResult = useMemo(() => {
    if (promptDependentInputs) return promptDependentInputs[`prompt.${slide.id}`];
  }, [promptDependentInputs, slide.id]);

  const { accessBadge: userAccessBadge } = useAccessBadge();
  const { isReady, promptPaywall, paywallComponent } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });
  const promptOutputs = useDesignerDataContext(allPromptOutputsSelector);

  const availablePromptDependencies = useMemo(() => {
    const questionField: LocalPromptDependency = {
      id: `slide-question`,
      type: 'live',
      label: `Slide question`,
      formType: 'shortText',
    };
    const lessonAttachmentDependencies = (isPassagesEnabled
      ? lessonAttachments || []
      : []
    ).map(attachment => {
      const dep: LocalPromptDependency = {
        id: attachment.attachmentId,
        type: 'live',
        label: parseSlateStringToText(attachment.title),
        formType: 'longText',
      };
      return dep;
    });
    if (!isGenerator) return [questionField, ...lessonAttachmentDependencies];

    const generalAvailablePromptDependencies = getAvailablePromptDependencies({
      promptId: prompt.id,
      promptOutputs,
      promptInputs,
    });

    return [
      ...generalAvailablePromptDependencies,
      questionField,
      ...lessonAttachmentDependencies,
    ];
  }, [
    isPassagesEnabled,
    lessonAttachments,
    isGenerator,
    prompt.id,
    promptOutputs,
    promptInputs,
  ]);

  const systemInstructionText = useMemo(() => {
    const systemMessage = prompt.messages.find(message => message.role === 'system');
    return systemMessage?.content;
  }, [prompt]);
  const question = useMemo(() => {
    const aiQuestionElement = slide.elements.find(
      element =>
        element.lockedFromDeletion &&
        (element.type === 'text' || element.type === 'generatorText'),
    );
    if (aiQuestionElement) {
      return parseSlateStringToText(aiQuestionElement.textValue);
    }
  }, [slide]);
  const dependencies = useMemo(() => findDependencies(prompt), [prompt]);
  const updatePromptDependentInputs = useCallback(
    (payload: { id: string; value: string; type: PromptSingleTestCaseResultType }) => {
      dispatch({
        type: DesignerUIStateActions.updatePromptDependentInputs,
        payload,
      });
    },
    [dispatch],
  );

  const { visibleInputs } = useVisiblePromptTestCaseInputs({
    availablePromptDependencies,
    promptDependencies: dependencies,
    promptInputs,
  });

  const { visiblePromptDependentInputValues } = useVisiblePromptDependentInputValues({
    visibleInputs,
    promptDependentInputs,
  });

  const promptContentSnippet = useMemo(() => {
    const userMessage = prompt.messages.find(message => message.role === 'user');
    if (!userMessage) return '';
    const content = parseSlateStringToText(userMessage.content);
    return `${content.slice(0, 59).trim()}....`;
  }, [prompt]);

  const handleImageSelected = useCallback(
    (data?: UploadFilePickerFile) => {
      if (!data) return;
      setImageToUpload(data);
      upload({
        imgData: data.src,
        fileId: data.fileId,
      });
    },
    [upload],
  );
  const handleDrawingCompleted = useCallback(
    async (
      imgData?: string,
      elements?: readonly ExcalidrawElement[],
      files?: BinaryFiles,
      api?: ExcalidrawImperativeAPI,
    ) => {
      if (!api) {
        return;
      }
      if (!elements || !elements.length) {
        return;
      }
      const canvas = await exportToCanvas({
        elements,
        appState: {
          ...api?.getAppState(),
          exportWithDarkMode: false,
        },
        files: api.getFiles(),
      });
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const canvaData = canvas.toDataURL();
      const fileId = generateUniqueId().toString();

      // convert the blob to a file;
      setShowDrawingMediaModal(false);
      setImageToUpload({
        src: canvaData,
        fileId,
        mimeType: 'image/png',
      });
      upload({
        imgData: canvaData,
        fileId,
      });
    },
    [upload],
  );

  const handleGenerateSinglePromptTestCase = useCallback(() => {
    if (!imageToUpload) return;
    let testCaseInputs: ById<string> = {};

    // add default values form the dependencies themselves
    dependencies.forEach(dependency => {
      const foundInput = promptInputs?.find(
        input => input.id === dependency.dependencyId,
      );

      if (foundInput && foundInput.defaultValue) {
        testCaseInputs = {
          ...testCaseInputs,
          [dependency.dependencyId]: foundInput.defaultValue as string,
        };
      }
    });

    // Overwrite the default values with the prompt dependent inputs
    Object.values(visiblePromptDependentInputValues).forEach(dependency => {
      testCaseInputs = {
        ...testCaseInputs,
        [dependency.id]: dependency.value,
      };
    });
    const inputData = Object.keys(testCaseInputs).filter(key => key.startsWith('input'));
    const outputData = Object.keys(testCaseInputs).filter(key =>
      key.startsWith('prompt'),
    );
    const userInputFormData = inputData.map(key => ({
      id: key,
      type: 'string',
      value: testCaseInputs[key],
    }));
    userInputFormData.push({
      id: 'slide-question-answer',
      type: 'string',
      value: `game_files/${imageToUpload.fileId}`,
    });
    userInputFormData.push({
      id: 'slide-question',
      type: 'string',
      value: parseSlateStringToText(question),
    });
    generate({
      presentationId,
      slideId: slide.id,
      userInputFormData,
      resourceMap: outputData.reduce(
        (acc, key) => ({
          ...acc,
          [key]: {
            id: key,
            type: 'string',
            value: testCaseInputs[key],
          },
        }),
        {},
      ),
    });
  }, [
    dependencies,
    generate,
    imageToUpload,
    presentationId,
    promptInputs,
    question,
    slide.id,
    visiblePromptDependentInputValues,
  ]);
  const handleGenerate = () => {
    handleGenerateSinglePromptTestCase();
  };

  const promptTemplate = useMemo(() => {
    return getDrawingPromptTemplate(prompt);
  }, [prompt]);
  const existingImageElement = useMemo<SlideElementImageType | undefined>(() => {
    const drawingBackgroundContextElement = slide.contextElements.find(
      el => el.purpose === 'drawingBackground',
    );
    if (!drawingBackgroundContextElement) {
      return undefined;
    }
    return slide.elements.find(
      el => el.id === drawingBackgroundContextElement.elementId,
    ) as SlideElementImageType | undefined;
  }, [slide]);

  const existingDrawing = useMemo<ExistingDrawing | undefined>(() => {
    if (existingImageElement) {
      return {
        backgroundDrawingFileId: slide.backgroundDrawingFileId,
        backgroundDrawingConfigFiles: slide.backgroundDrawingConfigFiles,
      };
    }
    return undefined;
  }, [slide, existingImageElement]);

  const testFeedbackDisabled = useMemo(() => {
    //check if there is any input that has not been filled
    const hasEmptyInput = visibleInputs.some(dependency => {
      return !visiblePromptDependentInputValues?.[dependency.id];
    });

    return hasEmptyInput;
  }, [visibleInputs, visiblePromptDependentInputValues]);

  const customizeTooltip = useMemo(() => {
    if (
      !userAccessBadge ||
      userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium
    ) {
      return 'Only available on the School plan.';
    }
    if (slide.generatorId && slide.generatorId !== 'dynamicLesson') {
      return `This lesson's AI feedback is pre-made by our Curriculum team and is not editable to ensure the best student experience.`;
    }

    return `As a Curipod ${
      userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise
        ? 'School plan'
        : 'Premium plan'
    } subscriber, you can customize this AI feedback.`;
  }, [slide.generatorId, userAccessBadge]);

  return (
    <>
      <StyledFormInputDiv>
        <Divider style={{ margin: '20px 0 10px 0' }} />
        <p style={{ margin: '5px 0 0 0', fontSize: 14 }}>Prompt template</p>
        <Select
          value={promptTemplate}
          disabled={
            slide.generatorId !== undefined &&
            (!isGenerator || slide.generatorId !== 'dynamicLesson')
          }
          iconValue={
            promptTemplate === 'custom' ? (
              <IconWrapper>
                <EnterprisePlanIcon
                  color={
                    isReady &&
                    (!userAccessBadge ||
                      userAccessBadge.license.coarseLevel ===
                        LicenseCoarseLevels.freemium)
                      ? theme.colors.yellow
                      : theme.colors.black
                  }
                />
              </IconWrapper>
            ) : (
              undefined
            )
          }
          onSelect={val => {
            metrics.logEvent('Creator.AiFeedback.ChangePromptTemplate', {
              promptTemplate: val,
            });
            if (val === 'custom') {
              if (
                isReady &&
                (!userAccessBadge ||
                  userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium)
              ) {
                metrics.logEvent('Creator.AiFeedback.ChangePromptTemplate.PaywallPrompt');
                promptPaywall({
                  label: 'Change prompt template',
                  description: 'AI Feedback Image',
                });
                return;
              }
            }

            const newPrompt = createAiMultiModalFeedbackPrompt(
              val as AiDrawingFeedbackType,
            );
            if (val === 'custom') {
              // Preserve existing prompt values when switching to custom
              newPrompt.promptOutput = prompt.promptOutput;
              newPrompt.messages = prompt.messages.map(message => ({
                ...message,
                content: message.content + '\n',
              }));
            }

            onRoundChange({
              ...slide,
              prompts: [newPrompt],
            });
          }}
          options={[
            //TODO: Add prompt templates for AI multi modal feedback
            {
              displayName: 'Science',
              value: 'science',
            },
            {
              displayName: 'Social studies',
              value: 'socialStudies',
            },
            {
              displayName: 'Math',
              value: 'math',
            },
            {
              displayName: 'Custom',
              value: 'custom',
              icon: (
                <IconWrapper>
                  <EnterprisePlanIcon
                    color={
                      isReady &&
                      (!userAccessBadge ||
                        userAccessBadge.license.coarseLevel ===
                          LicenseCoarseLevels.freemium)
                        ? theme.colors.yellow
                        : theme.colors.black
                    }
                  />
                </IconWrapper>
              ),
            },
          ]}
        />
        <Divider style={{ margin: '20px 0 10px 0' }} />
        {!isEditingPrompt && (
          <DisabledContainer
            onClick={() => {
              metrics.logEvent('Creator.AiFeedback.EditPrompt');
              if (
                !userAccessBadge ||
                userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium ||
                (slide.generatorId && slide.generatorId !== 'dynamicLesson')
              ) {
                metrics.logEvent('Creator.AiFeedback.EditPrompt.PaywallPrompt');
                promptPaywall({
                  label: 'Customize prompt',
                  description: 'AI Feedback Image',
                });
              } else {
                setIsEditingPrompt(true);
              }
            }}
          >
            {promptContentSnippet}
          </DisabledContainer>
        )}
        {!isEditingPrompt && (
          <span
            style={{ display: 'flex', cursor: 'pointer' }}
            onClick={() => {
              metrics.logEvent('Creator.AiFeedback.EditPrompt');
              if (
                !userAccessBadge ||
                userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium
              ) {
                metrics.logEvent('Creator.AiFeedback.EditPrompt.PaywallPrompt');
                promptPaywall({
                  label: 'Customize prompt',
                  description: 'AI Feedback Image',
                });
                return;
              }
              if (slide.generatorId && slide.generatorId !== 'dynamicLesson') {
                return;
              }
              setIsEditingPrompt(true);
            }}
          >
            {' '}
            <Tooltip title={customizeTooltip}>
              <span style={{ display: 'flex' }}>
                <IconWrapper
                  color={
                    !userAccessBadge ||
                    userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium
                      ? theme.colors.yellow
                      : 'currentColor'
                  }
                >
                  <EnterprisePlanIcon />
                </IconWrapper>
                <span style={{ textDecoration: 'underline', marginLeft: '5px' }}>
                  Customize
                </span>
              </span>
            </Tooltip>
          </span>
        )}
        {isEditingPrompt && (
          <EditSlidePrompt
            onRoundChange={onRoundChange}
            slide={slide}
            prompt={prompt}
            systemInstructionText={systemInstructionText}
            availablePromptDependencies={availablePromptDependencies}
            promptTemplate={promptTemplate}
          />
        )}
        <Divider style={{ margin: '20px 0 10px 0' }} />
        <div>
          <p style={{ margin: '10px 0', flex: 1, fontSize: 16 }}>Test the feedback</p>
          <p style={{ marginBottom: 5, fontSize: 14 }}>Question</p>

          {question && (
            <Tooltip
              title={'This question field is the same as the question field in the slide'}
              mouseEnterDelay={1}
            >
              <ActivityQuestion>{question}</ActivityQuestion>
            </Tooltip>
          )}

          {visibleInputs.map(dependency => {
            const dependencyId = dependency?.id;
            if (!dependencyId) return null;

            if (dependency.formType === 'dropdown') {
              return (
                <Select
                  label={dependency?.label}
                  placeholder={dependency?.defaultValue || 'Select value'}
                  onSelect={val => {
                    updatePromptDependentInputs({
                      id: dependencyId,
                      value: val as string,
                      type:
                        promptDependentInputs?.[dependencyId]?.type ||
                        dependency.formType,
                    });
                  }}
                  value={promptDependentInputs?.[dependencyId]?.value}
                  options={dependency?.constraints?.options || []}
                ></Select>
              );
            }

            if (dependency.formType === 'longText') {
              return (
                <div key={dependencyId}>
                  <TextArea
                    label={dependency?.label}
                    value={promptDependentInputs?.[dependencyId]?.value}
                    placeholder={dependency?.defaultValue || 'Add a value'}
                    style={{ width: '100%' }}
                    maxLength={dependency.constraints?.maxLength}
                    symbolCount={true}
                    onChange={e =>
                      updatePromptDependentInputs({
                        id: dependencyId,
                        value: e.target.value as string,
                        type:
                          promptDependentInputs?.[dependencyId]?.type ||
                          dependency.formType,
                      })
                    }
                  />
                </div>
              );
            }

            return (
              <div key={dependencyId}>
                <Input
                  label={dependency?.label}
                  value={promptDependentInputs?.[dependencyId]?.value}
                  placeholder={dependency?.defaultValue || 'Add a value'}
                  onChange={e =>
                    updatePromptDependentInputs({
                      id: dependencyId,
                      value: e.target.value as string,
                      type:
                        promptDependentInputs?.[dependencyId]?.type ||
                        dependency.formType,
                    })
                  }
                />
              </div>
            );
          })}
          <p style={{ margin: '10px 0', flex: 1, fontSize: 14 }}>Student answer</p>
          {showDrawingMediaModal && (
            <ModalStyled
              visible={showDrawingMediaModal}
              centered
              destroyOnClose
              keyboard={false}
              footer={null}
              closable={false}
              maskClosable={false}
              width={'90%'}
              style={{ paddingBottom: 0 }}
              bodyStyle={{ padding: 0 }}
              onCancel={() => setShowDrawingMediaModal(false)}
              onOk={() => setShowDrawingMediaModal(false)}
            >
              <ModalContainer>
                <SuspensePostHydration>
                  {/* This needs to be wrapped in a PostHydrationProvider and imported dynamic as excalidraw does not work SSR */}
                  <DrawingBuilder
                    onDrawingCompleted={handleDrawingCompleted}
                    onCancel={() => setShowDrawingMediaModal(false)}
                    existingDrawing={existingDrawing}
                  />
                </SuspensePostHydration>
              </ModalContainer>
            </ModalStyled>
          )}
          {imageToUpload && (
            <StudentAnswerContainer style={{ gridTemplateColumns: '1fr' }}>
              <img src={imageToUpload.src} style={{ width: '100%', height: 'auto' }} />
              <IconWrapper
                style={{ position: 'absolute', top: 0, right: 0 }}
                onClick={() => setImageToUpload(undefined)}
              >
                <DeleteSvg />
              </IconWrapper>
            </StudentAnswerContainer>
          )}
          {!imageToUpload && (
            <StudentAnswerContainer>
              <UploadFilePicker
                onFileSelected={handleImageSelected}
                onDelete={() => setImageToUpload(undefined)}
              />

              <Button onClick={() => setShowDrawingMediaModal(true)} type="ghost">
                Create on the whiteboard
              </Button>
            </StudentAnswerContainer>
          )}
          <Divider />

          <Button
            styles={{ margin: '10px 0' }}
            icon={<MagicWandSvg />}
            onClick={() => handleGenerate()}
            disabled={
              isUploading ||
              imageToUpload === undefined ||
              !isSuccess ||
              testFeedbackDisabled
            }
            loading={isLoading}
          >
            Test the feedback
          </Button>
          {testCaseResult && !isLoading && (
            <ResultsContainer>
              <span>Result</span>
              <p style={{ whiteSpace: 'pre-wrap' }}>{testCaseResult.value}</p>
            </ResultsContainer>
          )}
        </div>

        {paywallComponent}
      </StyledFormInputDiv>
    </>
  );
};
const EditSlidePrompt = ({
  onRoundChange,
  prompt,
  slide,
  availablePromptDependencies,
  systemInstructionText,
  promptTemplate,
}: {
  onRoundChange: (slide: Slide) => void;
  prompt: Prompt;
  slide: Slide;
  systemInstructionText?: string;
  promptTemplate?: string;
  availablePromptDependencies: LocalPromptDependency[];
}) => {
  const isAITemplatesEnabled = useFlag('AI_TEMPLATES');
  const metrics = useMetrics();

  const handleSystemInstructionChange = (val: string) => {
    const systemMessage = prompt.messages.find(message => message.role === 'system');
    if (systemMessage) {
      onRoundChange({
        ...slide,
        prompts: [
          {
            ...prompt,
            messages: prompt.messages.map(message =>
              message.role === 'system' ? { ...systemMessage, content: val } : message,
            ),
          },
        ],
      });
    }
  };

  const handlePromptMessageChanged = (val: string, index: number) => {
    const newMessage = { ...prompt.messages[index] };
    if (newMessage) {
      onRoundChange({
        ...slide,
        prompts: [
          {
            ...prompt,
            messages: prompt.messages.map((message, i) =>
              i === index ? { ...newMessage, content: val } : message,
            ),
          },
        ],
      });
    }
  };
  return (
    <>
      <Collapse defaultActiveKey={[]} ghost style={{ marginTop: '10px' }}>
        <Collapse.Panel
          header="Advanced options"
          key="1"
          style={{ paddingLeft: 0, fontWeight: 'bold' }}
        >
          {isAITemplatesEnabled && (
            <>
              <p style={{ paddingTop: '20px', fontSize: '14px' }}>Prompt model</p>
              <Select
                value={prompt.promptOutput.resource.model}
                onSelect={val => {
                  metrics.logEvent('Creator.AiFeedback.ChangePromptModel', {
                    modle: val,
                  });

                  onRoundChange({
                    ...slide,
                    prompts: [
                      {
                        ...prompt,
                        promptOutput: {
                          ...prompt.promptOutput,
                          resource: {
                            ...prompt.promptOutput.resource,
                            model: val as PromptModels,
                          },
                        },
                      },
                    ],
                  });
                }}
                options={[
                  {
                    displayName: 'GPT-4-turbo',
                    value: promptModels.gpt4Turbo,
                  },
                  {
                    displayName: 'GPT-4o',
                    value: promptModels.gpt4o,
                  },
                  {
                    displayName: 'GPT-4o-mini',
                    value: promptModels.gpt4omini,
                  },
                ]}
              />
            </>
          )}
          <p style={{ paddingTop: '20px', fontSize: '14px', display: 'flex' }}>
            Creativity: {prompt.temperature || 0.7}
            <Tooltip
              mouseEnterDelay={0.5}
              title={
                'High creativity means that the AI feedback will be more creative and less likely to be similar to other ai feedbacks, but also more unpredictable and more likely to be incorrect. A low temperature means the feedback will be more consistent, but at the expense of creativity, and it might feel stale'
              }
            >
              <IconWrapper width="15px" height="15px" style={{ marginLeft: '10px' }}>
                <QuestionMarkSvg />
              </IconWrapper>
            </Tooltip>
          </p>
          <Slider
            min={0}
            max={2}
            step={0.01}
            value={prompt.temperature || 0.7}
            onChange={e => {
              metrics.logEvent('Creator.AiFeedback.ChangePromptTemperature', {
                temperature: e,
              });

              onRoundChange({
                ...slide,
                prompts: [
                  {
                    ...prompt,
                    temperature: e,
                  },
                ],
              });
            }}
          />
          <Divider />
        </Collapse.Panel>
      </Collapse>
      <div style={{ display: 'flex', marginTop: '20px' }}>
        <p style={{ marginBottom: 5, flex: 1, fontSize: 14 }}>
          System context instruction
        </p>
      </div>
      <PromptContainer>
        <PromptTextEditor
          availablePromptDependencies={availablePromptDependencies}
          value={systemInstructionText}
          slideId={slide.id}
          promptTemplate={promptTemplate}
          maxLength={6000}
          placeholder={'You are a helpful assistant...'}
          onChange={handleSystemInstructionChange}
        />
      </PromptContainer>
      <div style={{ display: 'flex', marginTop: '20px' }}>
        <p style={{ marginBottom: 5, flex: 1, fontSize: 14 }}>Prompt instruction</p>
      </div>
      <MessageContainer>
        {prompt.messages.map((message, i) =>
          i === 0 ? null : (
            <div style={{ display: 'flex', height: '100%' }}>
              <div style={{ flex: 1 }}>
                <PromptContainer>
                  <PromptTextEditor
                    key={`${i}`}
                    value={message.content}
                    slideId={slide.id}
                    promptTemplate={promptTemplate}
                    placeholder={'Create a question about...'}
                    availablePromptDependencies={availablePromptDependencies}
                    onChange={val => handlePromptMessageChanged(val, i)}
                    maxLength={6000}
                  />
                </PromptContainer>
              </div>
            </div>
          ),
        )}
      </MessageContainer>
    </>
  );
};

const MessageContainer = styled.div`
  & > *:not(:first-child) {
    margin-top: 5px;
  }
  & > *:not(:last-child) {
    margin-bottom: 5px;
  }
`;

const StyledFormInputDiv = styled.div`
  margin-bottom: 20px;
  max-width: 100%;
  p {
    font-weight: bold;
    color: #282f33;
    margin-bottom: 5px;
  }
  .ant-collapse-header,
  .ant-collapse-content-box {
    padding: 0 !important;
  }
`;

const ActivityQuestion = styled.div`
  font-weight: bold;
  font-size: 12px;
  background-color: #e9ecef; /* Light gray background */
  color: #6c757d; /* Dark gray text */

  border: 1px solid #ced4da; /* Gray border */
  border-radius: 5px;
  padding: 10px;
  margin-bottom: 10px;
  margin-top: 10px;

  /* Optional: Add a "not-allowed" cursor to indicate that the field is disabled */
  cursor: not-allowed;
`;

const ModalContainer = styled.div`
  height: 100%;
  width: 100%;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  border-radius: 10px;
  h1 {
    font-weight: bold;
    text-align: center;
    margin-bottom: 20px;
  }

  @media (max-width: 900px) {
    h1 {
      font-size: 18px;
    }
  }
`;

const ResultsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  gap: 10px;
`;

const PromptContainer = styled.div`
  p {
    font-size: 14px;
    font-weight: bold;
    color: #282f33;
  }
`;

const DisabledContainer = styled.div`
  font-weight: bold;
  font-size: 12px;
  background-color: #e9ecef; /* Light gray background */
  color: #6c757d; /* Dark gray text */

  border: 1px solid #ced4da; /* Gray border */
  border-radius: 5px;
  padding: 10px;
  margin-bottom: 10px;
  margin-top: 10px;
  cursor: pointer;
`;

const StudentAnswerContainer = styled.div`
  display: grid;
  position: relative;
  grid-template-columns: 1fr;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 10px;
  justify-content: center;
`;

const ModalStyled = styled(Modal)`
  .ant-modal {
    padding-bottom: 0px;
  }
  .ant-modal-content {
    border-radius: 10px;
    box-shadow: none;
    height: 80vh;
  }
  .ant-modal-body {
    box-shadow: none;
    border-radius: 10px;
    background-color: ${theme.colors.white};
    height: 100%;
  }
`;
export default AiFeedbackImagePromptBuilder;
